import { createPortal } from "react-dom";
import { useCallback, useId, useState, type ReactNode } from "react";
import { Button, buttonVariants } from "./button";
import { Dialog, Tabs } from "./design-system";
import { isPdf, ResponsivePdfViewer } from "./responsive-pdf-viewer";

function documentName(name: string) {
  return /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}(?:\.[a-z\d]+)?$/i.test(name.trim()) ? "Dokument" : name;
}

export function DocumentViewer({ url, mime, title, filename, fallbackAction }: { url: string; mime: string; title: string; filename?: string; fallbackAction?: ReactNode }) {
  const [zoom, setZoom] = useState(100);
  const isImage = mime.startsWith("image/");
  return <div className="avm-document-viewer">
    {isImage ? <>
      <div className="avm-document-viewer__tools" aria-label="Zumiranje slike">
        <Button variant="secondary" size="sm" aria-label="Smanji" disabled={zoom <= 50} onClick={() => setZoom(value => value - 25)}>−</Button>
        <output aria-label="Uvećanje">{zoom} %</output>
        <Button variant="secondary" size="sm" aria-label="Povećaj" disabled={zoom >= 300} onClick={() => setZoom(value => value + 25)}>+</Button>
        <Button variant="tertiary" size="sm" onClick={() => setZoom(100)}>Prilagodi prikazu</Button>
      </div>
      <div className="avm-document-viewer__image"><img src={url} alt={title} style={{ width: `${zoom}%`, height: zoom === 100 ? "100%" : "auto", maxWidth: "none" }} /></div>
    </> : isPdf(mime)
      ? <ResponsivePdfViewer url={url} filename={documentName(filename || title)} title={documentName(title)} iframeSrc={`${url}#toolbar=1&navpanes=0&view=FitH`} fallbackAction={fallbackAction} />
      : <iframe src={`${url}#toolbar=1&navpanes=0&view=FitH`} title={documentName(title)} />}
  </div>;
}

/** Keeps comparison forms mounted when their portal host moves into fullscreen. */
export function DocumentWorkspace({ title: rawTitle, summary, url, mime = "application/pdf", filename, fallback, preview, actions, tabs = [], layout = "tabs" }: {
  title: string; summary?: ReactNode; url: string | null; mime?: string | null;
  filename?: string | null; fallback?: ReactNode; preview?: ReactNode; actions?: ReactNode;
  tabs?: { id: string; label: string; content: ReactNode }[];
  layout?: "tabs" | "comparison";
}) {
  const title = documentName(rawTitle);
  const [host] = useState(() => { const element = document.createElement("div"); element.className = "avm-document-comparison"; return element; });
  const dock = useCallback((node: HTMLDivElement | null) => { if (node) node.appendChild(host); }, [host]);
  const id = useId();
  const [active, setActive] = useState("preview");
  const [fullscreen, setFullscreen] = useState(false);
  const entries = [{ id: "preview", label: "Pregled", content: null }, ...tabs];
  const selected = entries.some(entry => entry.id === active) ? active : "preview";
  const header = (modal = false) => <header className="avm-document-summary">
    <h2>{title}</h2><div className="avm-document-summary__meta">{summary}</div>
    <div className="avm-document-summary__actions">
      {!modal && actions}
      {!modal && <Button variant="secondary" size="sm" disabled={!url} onClick={() => setFullscreen(true)}>Otvori u punom zaslonu</Button>}
      {url && <a className={buttonVariants({ variant: "secondary", size: "sm" })} href={url} download={documentName(filename || title)}>Preuzmi</a>}
    </div>
  </header>;
  const viewer = preview ?? (url ? <DocumentViewer url={url} mime={mime || "application/pdf"} filename={documentName(filename || title)} title={title === "Pregled dokumenta" ? title : `Pregled dokumenta ${title}`} /> : fallback);
  if (layout === "comparison") return <div className="avm-document-panel avm-surface">
    {header()}
    {!fullscreen && <div ref={dock} className="avm-document-dock" />}
    {createPortal(<>
      <section aria-label="Izvornik dokumenta" className="avm-document-source">{viewer}</section>
      <aside aria-label="Podaci i provjera" className="avm-document-details">{tabs.map(entry => <section key={entry.id} aria-label={entry.label} className="avm-document-detail-section"><h3>{entry.label}</h3>{entry.content}</section>)}</aside>
    </>, host)}
    {fullscreen && <Dialog fullscreen className="avm-document-dialog" title={title} onClose={() => setFullscreen(false)}>{header(true)}<div ref={dock} className="avm-document-dock" /></Dialog>}
  </div>;
  return <div className="avm-document-panel avm-surface">
    {header()}
    {entries.length > 1 && <Tabs className="avm-document-tabs" aria-label="Sadržaj dokumenta">
      {entries.map((entry, index) => <button key={entry.id} id={`${id}-${entry.id}`} role="tab" aria-selected={selected === entry.id} aria-controls={`${id}-${entry.id}-panel`} tabIndex={selected === entry.id ? 0 : -1} className={`avm-tab${selected === entry.id ? " is-active" : ""}`} onClick={() => setActive(entry.id)} onKeyDown={event => {
        const next = event.key === "ArrowRight" ? (index + 1) % entries.length : event.key === "ArrowLeft" ? (index + entries.length - 1) % entries.length : event.key === "Home" ? 0 : event.key === "End" ? entries.length - 1 : null;
        if (next !== null) { event.preventDefault(); setActive(entries[next]!.id); document.getElementById(`${id}-${entries[next]!.id}`)?.focus(); }
      }}>{entry.label}</button>)}
    </Tabs>}
    {entries.map(entry => <div key={entry.id} id={`${id}-${entry.id}-panel`} role={entries.length > 1 ? "tabpanel" : undefined} aria-labelledby={entries.length > 1 ? `${id}-${entry.id}` : undefined} hidden={selected !== entry.id} className={`avm-document-tab${entry.id === "preview" ? " avm-document-tab--preview" : " p-3"}`}>
      {entry.id === "preview" ? !fullscreen && viewer : entry.content}
    </div>)}
    {fullscreen && <Dialog fullscreen className="avm-document-dialog" title={title} onClose={() => setFullscreen(false)}>{header(true)}{viewer}</Dialog>}
  </div>;
}
